import { Button, Col, Row, Skeleton, Timeline } from 'antd';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IdentificationsFormInputs } from 'components/Identifications/types/IdentificationsTypes';
import { CardsFiltersParam } from 'components/cards/interfaces/CardsFiltersParam';
import { NewCardParams } from 'components/cards/interfaces/NewCardParams';
import { OwnershipLevelType } from 'interfaces/card';
import {
  CardHolderInfoContainer,
  CardHolderSectionHeader, CardHolderFormSectionTitle,
} from '../components/card-holder.styles';
import withAuthorization from '../components/hocs/withAuthorization';
import Page from '../components/page';
import useGetCardHolder from '../hooks/useGetCardHolder';
import {
  AddressType,
  CardHolderType, CountryCode,
} from '../interfaces/card-holder';
import DataDisplay from '../components/data-display';
import { Countries, RealmRoles } from '../constants';
import Addresses from '../components/Addresses/addresses';
import { AddressesFormInputs } from '../components/Addresses/modal-addresses';
import Identifications from '../components/Identifications/identifications';
import Phones, { PhoneFormInputs } from '../components/Phones/phones';
import CardsTable from '../components/cards/cards-table';
import CardHolderEditForm from './Cardholder/CardHolderEditForm';
import ContactCenterLogsTable from 'components/ContactCenterLogs/contact_center_logs_table';
import useAuthService from 'hooks/useAuthService';

const TODAY_DATE = new Date();
const LAST_3_MONTH_DATE = new Date(new Date().setMonth(TODAY_DATE.getMonth() - 3));

const CardHolderInfo = () => {
  const { id } = useParams<{ id: string }>();
  const { userHasRole } = useAuthService();
  const [isEditMode, setIsEditMode] = useState(false);
  const { cardHolder, isLoading, getCardholder } = useGetCardHolder(id, true);
  const isPerson = cardHolder?.type === CardHolderType.Person;

  const [addresses, setAddresses] = useState<AddressesFormInputs[]>();
  const [identifications, setIdentifications] = useState<IdentificationsFormInputs[]>();
  const [phones, setPhones] = useState<PhoneFormInputs[]>()

  var cardSearchParams: CardsFiltersParam = {}
  var newCardParams: NewCardParams = {}

  var cardSearchParamsPersonDescentralized: CardsFiltersParam = {}
  var newCardParamsPersonDescentralized: NewCardParams = {}


  if(isPerson){
    cardSearchParams = {
      cardholder: id
    }

    newCardParams= {
      cardholder: id,
      ownershipLevel: OwnershipLevelType.Main
    }

    if(cardHolder.centralized_balance){
      cardSearchParamsPersonDescentralized = {
        cardholderParent: id,
        ownershipLevel: OwnershipLevelType.Additional
      }

      newCardParamsPersonDescentralized= {
        cardholderParent: id,
        ownershipLevel: OwnershipLevelType.Additional
      }
    }

  } else {
    cardSearchParams = {
      cardholderParent: id,
      ownershipLevel: OwnershipLevelType.Additional
    }

    newCardParams= {
      cardholderParent: id,
      ownershipLevel: OwnershipLevelType.Additional
    }
  }



  const onEditBtnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();

    setIsEditMode(!isEditMode);
  };


  function getFormatDate() {
    const dt = new Date(cardHolder?.[isPerson ? 'birth_date' : 'inscription_date'] ?? new Date());

    return format(new Date(dt.getFullYear(), dt.getUTCMonth(), dt.getUTCDate()), 'dd/MM/yyyy');
  }


  useEffect( () => {
    if(cardHolder?.addresses) {
      setAddresses(() => {
        return cardHolder?.addresses.map((addr) => {
          return {
            type: addr?.type as AddressType,
            street: addr?.street,
            streetNumber: addr?.street_number,
            city: addr?.city,
            state: addr?.state,
            country: addr?.country as CountryCode,
            postalCode: addr?.postal_code,
            floor: addr?.floor,
            apt: addr?.apt
          };
        })
      })
    }

    if(cardHolder?.identifications) {
      // @ts-ignore
      setIdentifications( () => {
        return cardHolder?.identifications.map((idf) => {
          return {
            type: idf?.type,
            number: idf?.number,
            country: idf?.country,
            registeredVatPayer: idf?.registered_vat_payer
          }
        })
      })
    }

    if(cardHolder?.phones) {
      // @ts-ignore
      setPhones( () => {
        return cardHolder?.phones.map( (phone) => {
          return {
            type: phone?.type,
            number: phone?.number,
            country: phone?.country,
            areaCode: phone?.area_code
          }
        })
      })
    }
  }, [cardHolder])


  return (
    <Page title="Datos del Titular">
      {isLoading ? (
        <Skeleton />
      ) : (
        <>
          <CardHolderSectionHeader>
            <CardHolderFormSectionTitle> {isPerson ? 'Personal' : 'Empresa'} </CardHolderFormSectionTitle>
            <Button
              style={{ display: isEditMode ? 'none' : 'inline' }}
              type="primary"
              onClick={onEditBtnClick}
              // @ts-ignore
              closeCallback={onEditBtnClick}
            >
              Editar
            </Button>
          </CardHolderSectionHeader>


          {!isEditMode ? (
            <>
              <CardHolderInfoContainer>
                <>
                  <DataDisplay
                    title="Id:"
                    value={cardHolder?.external_id ? cardHolder?.external_id : 'Ninguno'}
                  />
                  <DataDisplay
                    title={isPerson ? 'Apellido:' : 'Trade Name:'}
                    value={cardHolder?.[isPerson ? 'last_name' : 'trade_name']}
                  />
                  <DataDisplay title="Nombre:" value={cardHolder?.[isPerson ? 'first_name' : 'legal_name']} />
                  <DataDisplay
                    title={isPerson ? 'Fecha de Nacimiento:' : 'Fecha de Inscripción:'}
                    value={getFormatDate()} />
                  {isPerson && <DataDisplay title="Nacionalidad:" value={Countries[cardHolder?.nationality]} />}
                  {isPerson && <DataDisplay title="Genero:" value={cardHolder?.gender} />}
                  <DataDisplay title="Email:" value={cardHolder?.email} />
                </>
              </CardHolderInfoContainer>

              {isLoading ? (
                <Skeleton />
              ) : (
                <Row gutter={24}>
                  <Col span={24} >
                    <Identifications
                      identifications={identifications}
                      setIdentifications={setIdentifications}
                      cardHolderId={id}
                    />
                  </Col>
                  <Col span={24}>
                    <Addresses
                      addresses={addresses}
                      setAddresses={setAddresses}
                      cardHolderId={id}
                    />
                  </Col>
                  <Col span={24}>
                    <Phones
                      phones={phones}
                      setPhones={setPhones}
                      cardHolderId={id}
                    />
                  </Col>
                </Row>
              )}
            </>
          ) :
            <CardHolderEditForm
              id={id}
              cardHolder={cardHolder}
              isPerson={isPerson}
              closeCallback={() => {
                setIsEditMode(!isEditMode);
              }}
              getCardholder={getCardholder}
            />
          }

          {/* MIS TARJETAS */}
          { isPerson && (
            <CardsTable
              searchParam={cardSearchParams}
              newCardParams= {newCardParams}
              style={{marginTop:'1em'}}
              showNominateCard={true}
            />
          )}

          {/* ADICIONALES - para descentralizado se visualizan en el detalle de la tarjeta */}
          {
            cardHolder?.centralized_balance && (
              <CardsTable
                style={{marginTop:'1em'}}
                searchParam={cardSearchParamsPersonDescentralized}
                newCardParams= {newCardParamsPersonDescentralized}
                tableTitle="Adicionales"
              />
            )
          }
        </>
      )}

      {userHasRole([RealmRoles.ContactCenter]) && (
        <ContactCenterLogsTable
        searchParam={{
          cardholder_id: id,
          start_date: TODAY_DATE.toDateString(),
          end_date: LAST_3_MONTH_DATE.toDateString(),
          page: 1,
          size: 10
          }}
          style={{marginTop:'1em'}}
          />
        )}
    </Page>
  );
};

export default withAuthorization([RealmRoles.CardHolderAllRead], CardHolderInfo);
